import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Tabs,
  Tab,
  Typography,
  AppBar,
  Toolbar,
  Chip,
  IconButton,
  Badge,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Security,
  TrendingUp,
  Notifications,
  Refresh,
  Circle,
  ShowChart,
} from '@mui/icons-material';
import PortfolioPanel from './components/PortfolioPanel';
import RiskPanel from './components/RiskPanel';
import StrategyPanel from './components/StrategyPanel';
import BacktestPanel from './components/BacktestPanel';
import type { Alert as AlertType } from './types';
import { fetchAlerts } from './services/api';

function App() {
  const [activeTab, setActiveTab] = useState(0);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [statusDot, setStatusDot] = useState<'green' | 'yellow' | 'red'>('green');

  const loadAlerts = useCallback(async () => {
    try {
      const data = await fetchAlerts();
      setAlerts(data);
      setUnreadCount(data.filter((a) => !a.acknowledged).length);
      setStatusDot('green');
    } catch {
      setStatusDot('red');
    }
  }, []);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 30_000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  const handleTabChange = (_: React.SyntheticEvent, val: number) => setActiveTab(val);

  const notify = (msg: string) => {
    setSnackbarMsg(msg);
    setSnackbarOpen(true);
  };

  const criticalAlerts = alerts.filter((a) => a.level === 'critical' && !a.acknowledged);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* ── Top App Bar ── */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: '#1a1d27',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #6366f1, #22d3ee)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '1rem',
              }}
            >
              H
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
              Hermes Trader
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Circle sx={{ fontSize: 10, color: `${statusDot}.main`, mr: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              {statusDot === 'green' ? 'All systems operational' : statusDot === 'yellow' ? 'Degraded' : 'Offline'}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Chip
            size="small"
            label={`Updated ${lastRefresh.toLocaleTimeString()}`}
            sx={{ backgroundColor: 'background.paper', fontSize: '0.7rem' }}
          />

          <Tooltip title="Refresh data">
            <IconButton
              size="small"
              onClick={() => { loadAlerts(); setLastRefresh(new Date()); notify('Data refreshed'); }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Alerts">
            <IconButton size="small">
              <Badge badgeContent={unreadCount} color="error">
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </Toolbar>

        {/* Critical alert banner */}
        {criticalAlerts.length > 0 && (
          <Box sx={{ px: 2, pb: 1 }}>
            {criticalAlerts.slice(0, 2).map((a) => (
              <Alert key={a.id} severity="error" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                {a.title}: {a.message}
              </Alert>
            ))}
          </Box>
        )}
      </AppBar>

      {/* ── Main Content ── */}
      <Container maxWidth={false} sx={{ py: 2, maxWidth: 1600 }}>
        {/* ── Tab Navigation ── */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                minHeight: 48,
                gap: 1,
              },
            }}
          >
            <Tab icon={<AccountBalanceWallet fontSize="small" />} label="Portfolio" />
            <Tab icon={<Security fontSize="small" />} label="Risk" />
            <Tab icon={<TrendingUp fontSize="small" />} label="Strategy" />
            <Tab icon={<ShowChart fontSize="small" />} label="Backtest" />
          </Tabs>
        </Box>

        {/* ── Tab Panels ── */}
        <Grid container spacing={2}>
          {activeTab === 0 && <PortfolioPanel onNotify={notify} />}
          {activeTab === 1 && <RiskPanel onNotify={notify} />}
          {activeTab === 2 && <StrategyPanel onNotify={notify} />}
          {activeTab === 3 && <BacktestPanel />}
        </Grid>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2500}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default App;
